import React, { useEffect, useMemo, useState } from "react";
import { CloseButton, Offcanvas, Spinner } from "react-bootstrap";
import { Button as AntButton, Select } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "contexts/AuthContext";
import CopyableText from "components/common/CopyableText";
import useFormattedDate from "hooks/useFormatDate";
import { toast } from "react-toastify";

const STATUS_STYLES = {
  ACTIVE: { bg: "#e8f7e3", text: "#2e7d32", border: "#d5ebd0" },
  DISABLED: { bg: "#fbe9e7", text: "#c62828", border: "#f5c6cb" },
  DEFAULT: { bg: "#f5f5f5", text: "#616161", border: "#e0e0e0" },
};

const SectionLabel = ({ children }) => (
  <div className="text-600 text-uppercase fw-semibold mb-2" style={{ fontSize: 11, letterSpacing: 0.6 }}>
    {children}
  </div>
);

const DetailItem = ({ label, children }) => (
  <div className="mb-3">
    <SectionLabel>{label}</SectionLabel>
    <div className="rounded-3 px-3 py-2 text-700" style={{ fontSize: 13, wordBreak: "break-word", backgroundColor: "#f5f7fa" }}>
      {children || "--"}
    </div>
  </div>
);

const StatusPill = ({ active }) => {
  const label = active === true ? "ACTIVE" : "DISABLED";
  const ui = STATUS_STYLES[label] || STATUS_STYLES.DEFAULT;
  return (
    <span
      style={{
        backgroundColor: ui.bg,
        color: ui.text,
        border: `1px solid ${ui.border}`,
        borderRadius: 999,
        padding: "5px 12px",
        fontSize: 11,
        fontWeight: 700,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: ui.text,
          animation: "statusDotBlink 2.2s ease-in-out infinite",
        }}
      />
      {label}
    </span>
  );
};

const getStatusPanelStyle = (active) => {
  const normalized = active === true ? "ACTIVE" : "DISABLED";

  if (normalized === "ACTIVE") {
    return {
      bg: "#f3fbf6",
      border: "#d1f2e0",
      heading: "#166534",
    };
  }

  const ui = STATUS_STYLES[normalized] || STATUS_STYLES.DEFAULT;
  return {
    bg: ui.bg,
    border: ui.border,
    heading: ui.text,
  };
};

export const ApiKeyDetailsDrawer = ({
  selectedKey,
  loading,
  showDrawer,
  setShowDrawer,
  updateApiKey,
}) => {
  const [visible, setVisible] = useState(false);
  const [isEditingMode, setIsEditingMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeStatus, setActiveStatus] = useState(null);
  const { user } = useAuth();
  const { formatDateTime } = useFormattedDate();

  useEffect(() => {
    if (showDrawer) {
      setVisible(true);
      setIsEditingMode(false);
    }
  }, [showDrawer]);

  useEffect(() => {
    setActiveStatus(selectedKey?.active ?? null);
  }, [selectedKey]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => setShowDrawer(false), 250);
  };

  const handleSaveChanges = async () => {
    if (!selectedKey?.id || activeStatus === null) return;
    try {
      setIsSubmitting(true);
      await updateApiKey(selectedKey.id, {
        ...selectedKey,
        active: activeStatus,
        updatedAt: new Date().toISOString(),
        updatedBy: user?.id,
      });
      setIsEditingMode(false);
    } catch (err) {
      toast.error("Failed to update API key");
    } finally {
      setIsSubmitting(false);
    }
  };

  const statusOptions = useMemo(
    () => [
      { label: "Activate", value: true },
      { label: "Disable", value: false },
    ],
    []
  );

  const currentStatusValue = isEditingMode ? activeStatus : selectedKey?.active;
  const statusPanelStyle = getStatusPanelStyle(currentStatusValue);

  return (
    <Offcanvas
      show={visible}
      onHide={handleClose}
      placement="end"
      className="border-0"
      style={{ width: "31rem", maxWidth: "100vw" }}
    >
      <style>
        {`
          @keyframes statusDotBlink {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.35; transform: scale(0.85); }
          }
        `}
      </style>

      <div className="px-4 py-3" style={{ borderBottom: "1px solid #d8e2ef", backgroundColor: "#f9fbfd" }}>
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h5 className="fw-bold mb-1">API Key Details</h5>
            <div className="text-600" style={{ fontSize: 12 }}>
              Key metadata and status configuration
            </div>
          </div>
          <CloseButton onClick={handleClose} />
        </div>
      </div>

      <div className="flex-grow-1 overflow-auto px-4 py-4" style={{ backgroundColor: "#f9fbfd" }}>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
            <Spinner animation="border" variant="success" />
          </div>
        ) : selectedKey ? (
          <div className="bg-white rounded-4 p-4" style={{ border: "1px solid #d8e2ef", boxShadow: "0 4px 12px rgba(15, 23, 42, 0.04)" }}>
            <div
              className="mb-4 p-3 rounded-3"
              style={{
                backgroundColor: statusPanelStyle.bg,
                border: `1px solid ${statusPanelStyle.border}`,
              }}
            >
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div
                  className="text-uppercase fw-semibold"
                  style={{
                    fontSize: 11,
                    letterSpacing: 0.6,
                    color: statusPanelStyle.heading,
                  }}
                >
                  Status
                </div>
                <span className="text-500" style={{ fontSize: 11 }}>Editable</span>
              </div>
              {isEditingMode ? (
                <Select
                  size="middle"
                  style={{ width: "100%", maxWidth: 240 }}
                  value={activeStatus}
                  options={statusOptions}
                  onChange={setActiveStatus}
                />
              ) : (
                <StatusPill active={selectedKey.active} />
              )}
            </div>

            <DetailItem label="API Key ID">
              <CopyableText value={selectedKey.id} />
            </DetailItem>
            <DetailItem label="Name">{selectedKey.name}</DetailItem>
            <DetailItem label="Client ID">
              <CopyableText value={selectedKey.clientId} />
            </DetailItem>
            <DetailItem label="Key">
              <CopyableText value={selectedKey.key} />
            </DetailItem>
            <DetailItem label="Expires At">{formatDateTime(selectedKey.expiresAt)}</DetailItem>
            <DetailItem label="Description">{selectedKey.description}</DetailItem>
            <DetailItem label="Updated At">{formatDateTime(selectedKey.updatedAt)}</DetailItem>
            <DetailItem label="Updated By">{selectedKey.updatedBy}</DetailItem>
          </div>
        ) : (
          <div className="text-center text-600 py-5">No API key details available.</div>
        )}
      </div>

      <div className="border-top px-4 py-3" style={{ borderColor: "#d8e2ef", backgroundColor: "#fff" }}>
        {!isEditingMode ? (
          <div className="d-flex justify-content-between align-items-center">
            <AntButton
              type="primary"
              className="bg-success border-0 text-white"
              icon={<FontAwesomeIcon icon={faEdit} />}
              onClick={() => setIsEditingMode(true)}
            >
              Edit Status
            </AntButton>
            <AntButton
              className="bg-secondary text-white border-0 text-xs fw-bold"
              onClick={handleClose}
            >
              Close
            </AntButton>
          </div>
        ) : (
          <div className="d-flex justify-content-between align-items-center">
            <AntButton
              type="primary"
              className="bg-success border-0 text-white"
              loading={isSubmitting}
              onClick={handleSaveChanges}
            >
              Save Changes
            </AntButton>
            <AntButton
              onClick={() => {
                setActiveStatus(selectedKey?.active ?? null);
                setIsEditingMode(false);
              }}
            >
              Cancel
            </AntButton>
          </div>
        )}
      </div>
    </Offcanvas>
  );
};
